import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const EMPTY_FORM = {
  name: "",
  email: "",
  phone: "",
  role: "TENANT",
  flatNumber: "",
  block: "",
  floor: "",
  squareFeet: "",
  dateOfPropertyPurchase: "",
  occupancyStatus: "",
  ownerName: "",
  propertyType: "",
  phoneNumber: "",
  whatsappNumber: "",
  ownerPhoneNumber: "",
  ownerWhatsappNumber: "",
  jointOwnerPhoneNumber: "",
  jointOwnerWhatsappNumber: "",
  advanceAmount: "",
  rentAmount: "",
  maintenanceAmount: "",
  tenureStartDate: "",
  tenureEndDate: ""
};

const pickTenant = (data) => {
  if (!data || typeof data !== "object") return null;
  if (data.tenant && typeof data.tenant === "object") return data.tenant;
  if (data.result && typeof data.result === "object") return data.result;
  if (data._id || data.id || data.userid || data.email) {
    const { status, message, success, ...rest } = data;
    return Object.keys(rest).length ? rest : data;
  }
  return null;
};

const toInputDate = (iso) => {
  if (iso == null || iso === "") return "";
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const tenantToForm = (t) => ({
  name: t?.name || "",
  email: t?.email || "",
  phone: t?.phone || "",
  role: t?.role || "TENANT",
  flatNumber: t?.flatNumber || "",
  block: t?.block || "",
  floor: t?.floor != null && t?.floor !== "" ? String(t.floor) : "",
  squareFeet: t?.squareFeet != null && t?.squareFeet !== "" ? String(t.squareFeet) : "",
  dateOfPropertyPurchase: toInputDate(t?.dateOfPropertyPurchase),
  occupancyStatus: t?.occupancyStatus || "",
  ownerName: t?.ownerName || "",
  propertyType: t?.propertyType || "",
  phoneNumber: t?.phoneNumber || "",
  whatsappNumber: t?.whatsappNumber || "",
  ownerPhoneNumber: t?.ownerPhoneNumber || "",
  ownerWhatsappNumber: t?.ownerWhatsappNumber || "",
  jointOwnerPhoneNumber: t?.jointOwnerPhoneNumber || "",
  jointOwnerWhatsappNumber: t?.jointOwnerWhatsappNumber || "",
  advanceAmount: t?.advanceAmount != null && t?.advanceAmount !== "" ? String(t.advanceAmount) : "",
  rentAmount: t?.rentAmount != null && t?.rentAmount !== "" ? String(t.rentAmount) : "",
  maintenanceAmount:
    t?.maintenanceAmount != null && t?.maintenanceAmount !== "" ? String(t.maintenanceAmount) : "",
  tenureStartDate: toInputDate(t?.tenureStartDate),
  tenureEndDate: toInputDate(t?.tenureEndDate)
});

const SectionTitle = ({ children, first }) => (
  <div
    className="text-muted text-uppercase"
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      marginTop: first ? 0 : 18,
      marginBottom: 8
    }}
  >
    {children}
  </div>
);

const TenantreportEdit = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { tenantId: tenantIdParam } = useParams();
  const tenantId = useMemo(() => {
    try {
      return tenantIdParam ? decodeURIComponent(String(tenantIdParam)).trim() : "";
    } catch {
      return String(tenantIdParam || "").trim();
    }
  }, [tenantIdParam]);

  const userid = useMemo(() => {
    try {
      return String(localStorage.getItem("userid") || "").trim();
    } catch {
      return "";
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    if (!tenantId) {
      toast.error("Invalid tenant link.");
      navigate("/tenant-report", { replace: true });
    }
  }, [tenantId, navigate]);

  const fetchTenant = useCallback(async () => {
    if (!API_BASE_URL || !tenantId) {
      if (!API_BASE_URL) toast.error("Missing REACT_APP_API_URL in environment");
      setLoading(false);
      return;
    }
    if (!userid) {
      toast.error("Missing userid. Please login again.");
      setLoading(false);
      return;
    }

    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "admin.getTenant",
        userid,
        tenantId
      });
      const data = response?.data;

      if (data?.status === "success") {
        const t = pickTenant(data);
        if (t) {
          setForm(tenantToForm(t));
        } else {
          toast.error(data?.message || "Tenant data not found in response");
        }
      } else {
        toast.error(data?.message || "Failed to load tenant");
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Something went wrong");
    } finally {
      if (didIncrement) loaderDecrement();
      setLoading(false);
    }
  }, [API_BASE_URL, tenantId, userid]);

  useEffect(() => {
    if (!tenantId) return;
    fetchTenant();
  }, [tenantId, fetchTenant]);

  const onChange = (key) => (e) => {
    const value = e?.target?.value ?? "";
    setForm((f) => ({ ...f, [key]: value }));
  };

  const onNumberChange = (key) => (e) => {
    const raw = e?.target?.value ?? "";
    setForm((f) => ({ ...f, [key]: raw === "" ? "" : raw }));
  };

  const validate = () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return false;
    }
    if (!form.email.trim()) {
      toast.error("Email is required");
      return false;
    }
    if (!form.phone.trim()) {
      toast.error("Phone is required");
      return false;
    }
    if (!form.block) {
      toast.error("Block is required");
      return false;
    }
    if (!form.floor) {
      toast.error("Floor is required");
      return false;
    }
    if (!form.flatNumber.trim()) {
      toast.error("Flat number is required");
      return false;
    }
    if (!form.squareFeet || Number(form.squareFeet) <= 0) {
      toast.error("Enter a valid square feet");
      return false;
    }
    if (!form.dateOfPropertyPurchase) {
      toast.error("Date of property purchase is required");
      return false;
    }
    if (!form.ownerName.trim()) {
      toast.error("Owner name is required");
      return false;
    }
    if (!form.propertyType) {
      toast.error("Property type is required");
      return false;
    }
    if (!form.occupancyStatus) {
      toast.error("Occupancy status is required");
      return false;
    }
    if (form.advanceAmount !== "") {
      const advanceAmount = Number(form.advanceAmount);
      if (!Number.isFinite(advanceAmount) || advanceAmount < 0) {
        toast.error("Enter a valid advance amount");
        return false;
      }
    }
    if (form.rentAmount !== "") {
      const rentAmount = Number(form.rentAmount);
      if (!Number.isFinite(rentAmount) || rentAmount < 0) {
        toast.error("Enter a valid rent amount");
        return false;
      }
    }
    if (form.maintenanceAmount !== "") {
      const maintenanceAmount = Number(form.maintenanceAmount);
      if (!Number.isFinite(maintenanceAmount) || maintenanceAmount < 0) {
        toast.error("Enter a valid maintenance amount");
        return false;
      }
    }
    if (form.tenureStartDate && form.tenureEndDate && form.tenureEndDate < form.tenureStartDate) {
      toast.error("Tenure end date must be on or after start date");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      return;
    }
    if (!userid) {
      toast.error("Missing userid. Please login again.");
      return;
    }

    setSubmitting(true);
    let didIncrement = false;
    try {
      const payload = {
        action: "admin.updateTenant",
        userid,
        tenantId,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        role: form.role || "TENANT",
        flatNumber: form.flatNumber.trim(),
        block: form.block,
        floor: Number(form.floor),
        squareFeet: Number(form.squareFeet),
        dateOfPropertyPurchase: form.dateOfPropertyPurchase,
        occupancyStatus: form.occupancyStatus,
        ownerName: form.ownerName.trim(),
        propertyType: form.propertyType,
        phoneNumber: form.phoneNumber.trim(),
        whatsappNumber: form.whatsappNumber.trim(),
        ownerPhoneNumber: form.ownerPhoneNumber.trim(),
        ownerWhatsappNumber: form.ownerWhatsappNumber.trim(),
        jointOwnerPhoneNumber: form.jointOwnerPhoneNumber.trim(),
        jointOwnerWhatsappNumber: form.jointOwnerWhatsappNumber.trim(),
        advanceAmount: form.advanceAmount === "" ? null : Number(form.advanceAmount),
        rentAmount: form.rentAmount === "" ? null : Number(form.rentAmount),
        maintenanceAmount: form.maintenanceAmount === "" ? null : Number(form.maintenanceAmount),
        tenureStartDate: form.tenureStartDate || null,
        tenureEndDate: form.tenureEndDate || null
      };

      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, payload);
      const data = response?.data;

      if (data?.success === true || data?.status === "success") {
        toast.success(data?.message || "Tenant updated successfully");
        navigate(`/tenant-report/view/${encodeURIComponent(tenantId)}`);
      } else {
        toast.error(data?.message || "Failed to update tenant");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Something went wrong");
    } finally {
      if (didIncrement) loaderDecrement();
      setSubmitting(false);
    }
  };

  if (!tenantId) {
    return null;
  }

  const viewUrl = `/tenant-report/view/${encodeURIComponent(tenantId)}`;

  return (
    <div className="employee-form tenant-report">
      <div className="content-header">
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
            <div>
              <h1 className="m-0 txt_lft">Edit tenant</h1>
              <p style={{ margin: 0, color: "#6c757d", fontSize: "14px" }}>
                Update tenant profile, residence, and charges
              </p>
            </div>
            <div className="d-flex gap-2">
              <Link to="/tenant-report" className="btn btn-outline-secondary">
                Back to list
              </Link>
             
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {loading ? (
            <div className="card">
              <div className="card-body text-muted">Loading tenant…</div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <SectionTitle first>Profile</SectionTitle>
                  <div className="row invite-form-row">
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Name <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter name"
                          value={form.name}
                          onChange={onChange("name")}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Email <span className="required-star">*</span>
                        </label>
                        <input
                          type="email"
                          className="form-control"
                          placeholder="Enter email"
                          value={form.email}
                          onChange={onChange("email")}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Phone <span className="required-star">*</span>
                        </label>
                        <input
                          type="tel"
                          className="form-control"
                          placeholder="Enter phone"
                          value={form.phone}
                          onChange={onChange("phone")}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">Role</label>
                        <input
                          type="text"
                          className="form-control"
                          value={form.role}
                          readOnly
                          tabIndex={-1}
                          aria-readonly="true"
                          style={{
                            backgroundColor: "#f1f3f5",
                            color: "#868e96",
                            cursor: "not-allowed"
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  <SectionTitle>Residence</SectionTitle>
                  <div className="row invite-form-row">
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Block <span className="required-star">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={form.block}
                          onChange={onChange("block")}
                          required
                        >
                          <option value="">Select Block</option>
                          <option value="A">A</option>
                          <option value="B">B</option>
                          <option value="C">C</option>
                          <option value="D">D</option>
                          <option value="E">E</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Floor <span className="required-star">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={form.floor}
                          onChange={onChange("floor")}
                          required
                        >
                          <option value="">Select Floor</option>
                          <option value="1">1</option>
                          <option value="2">2</option>
                          <option value="3">3</option>
                          <option value="4">4</option>
                          <option value="5">5</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Flat Number <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter flat number"
                          value={form.flatNumber}
                          onChange={onChange("flatNumber")}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Square Feet <span className="required-star">*</span>
                        </label>
                        <input
                          type="number"
                          min="1"
                          className="form-control"
                          placeholder="Enter square feet"
                          value={form.squareFeet}
                          onChange={onNumberChange("squareFeet")}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row invite-form-row">
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Property Purchase Date <span className="required-star">*</span>
                        </label>
                        <input
                          type="date"
                          className="form-control"
                          value={form.dateOfPropertyPurchase}
                          onChange={onChange("dateOfPropertyPurchase")}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Occupancy Status <span className="required-star">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={form.occupancyStatus}
                          onChange={onChange("occupancyStatus")}
                          required
                        >
                          <option value="">Select Status</option>
                          <option value="Self Occupied">Self Occupied</option>
                          <option value="Rent Out">Rent Out</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">Advance (₹)</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="form-control"
                          placeholder="Enter advance"
                          value={form.advanceAmount}
                          onChange={onNumberChange("advanceAmount")}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">Rent (₹)</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="form-control"
                          placeholder="Enter rent"
                          value={form.rentAmount}
                          onChange={onNumberChange("rentAmount")}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="row invite-form-row">
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">Maintenance (₹)</label>
                        <input
                          type="number"
                          min="0"
                          step="1"
                          className="form-control"
                          placeholder="Enter maintenance"
                          value={form.maintenanceAmount}
                          onChange={onNumberChange("maintenanceAmount")}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">Tenure Start</label>
                        <input
                          type="date"
                          className="form-control"
                          value={form.tenureStartDate}
                          onChange={onChange("tenureStartDate")}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">Tenure End</label>
                        <input
                          type="date"
                          className="form-control"
                          value={form.tenureEndDate}
                          onChange={onChange("tenureEndDate")}
                        />
                      </div>
                    </div>
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Owner Name <span className="required-star">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Enter owner name"
                          value={form.ownerName}
                          onChange={onChange("ownerName")}
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <SectionTitle>Property &amp; contact</SectionTitle>
                  <div className="row invite-form-row">
                    <div className="col-12 col-md-6 col-lg-3">
                      <div className="form-group">
                        <label className="form-label">
                          Property Type <span className="required-star">*</span>
                        </label>
                        <select
                          className="form-control"
                          value={form.propertyType}
                          onChange={onChange("propertyType")}
                          required
                        >
                          <option value="">Select Property Type</option>
                          <option value="Single Owner">Single Owner</option>
                          <option value="Joint Owner">Joint Owner</option>
                        </select>
                      </div>
                    </div>

                    {form.propertyType === "Single Owner" && (
                      <>
                        <div className="col-12 col-md-6 col-lg-3">
                          <div className="form-group">
                            <label className="form-label">Phone Number</label>
                            <input
                              type="tel"
                              className="form-control"
                              placeholder="Enter phone number"
                              value={form.phoneNumber}
                              onChange={onChange("phoneNumber")}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                          <div className="form-group">
                            <label className="form-label">WhatsApp Number</label>
                            <input
                              type="tel"
                              className="form-control"
                              placeholder="Enter WhatsApp number"
                              value={form.whatsappNumber}
                              onChange={onChange("whatsappNumber")}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {form.propertyType === "Joint Owner" && (
                      <>
                        <div className="col-12 col-md-6 col-lg-3">
                          <div className="form-group">
                            <label className="form-label">Owner Phone Number</label>
                            <input
                              type="tel"
                              className="form-control"
                              placeholder="Enter owner phone"
                              value={form.ownerPhoneNumber}
                              onChange={onChange("ownerPhoneNumber")}
                            />
                          </div>
                        </div>
                        <div className="col-12 col-md-6 col-lg-3">
                          <div className="form-group">
                            <label className="form-label">Owner WhatsApp Number</label>
                            <input
                              type="tel"
                              className="form-control"
                              placeholder="Enter owner WhatsApp number"
                              value={form.ownerWhatsappNumber}
                              onChange={onChange("ownerWhatsappNumber")}
                            />
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {form.propertyType === "Joint Owner" && (
                    <div className="row invite-form-row">
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="form-group">
                          <label className="form-label">Joint Owner Phone Number</label>
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Enter joint owner phone"
                            value={form.jointOwnerPhoneNumber}
                            onChange={onChange("jointOwnerPhoneNumber")}
                          />
                        </div>
                      </div>
                      <div className="col-12 col-md-6 col-lg-3">
                        <div className="form-group">
                          <label className="form-label">Joint Owner WhatsApp Number</label>
                          <input
                            type="tel"
                            className="form-control"
                            placeholder="Enter joint owner WhatsApp number"
                            value={form.jointOwnerWhatsappNumber}
                            onChange={onChange("jointOwnerWhatsappNumber")}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="row invite-form-row">
                    <div className="col-12">
                      <div className="form-group invite-form-actions mb-0">
                        <div className="d-flex flex-wrap justify-content-lg-end" style={{ gap: 10 }}>
                         
                          <Link to={viewUrl} className="btn btn-secondary">
                            Cancel
                          </Link>
                          <button type="submit" className="btn btn-primary" disabled={submitting}>
                            {submitting ? "Saving…" : "Save changes"}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantreportEdit;
